import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useGlobal from '../../../store';
import Container from '../../components/container';
import TariffGate from '../../components/tariff.gate';
import PriceRow from './price.row';
import './prices.scss';

const shortNames = {

  "Столешница деревянная": "Размер",
  "Демонтаж порога": { bricks: "Кирпичный дом", panel: "Панельный дом" },
  "Остекление": { standart: "Стандартное", premium: "Премиальное", lamination: "С ламинацией" },
  "Подоконник бюджетный": `Ширина $X$мм`,
  "Подоконник премиальный": `Ширина $X$мм`,
  "Сушилка для белья": `Размер $X$м`,
  "Откос: 1-2х створчатое комнатное окно": { bricks: "Кирпич", panel: "Панель" },
  "Откос: 3х створчатое комнатное окно": { bricks: "Кирпич", panel: "Панель" },
  "Откос: балконный выход с окном": { bricks: "Кирпич", panel: "Панель" },
  "Откос: балконный выход чебурашка": { bricks: "Кирпич", panel: "Панель" },
  "Перила из хромированных труб": { m3: "3м балкон", m6: "6м балкон" },
  "Расходники по обшивке": { m3: "3м балкон", m6: "6м балкон" },
  "Шкаф": {
    tumbMax13: "Ларь или тумба < 1.3м.",
    tumbMax20: "Тумба 1.3м - 2м.",
    cupbMax17: "Шкаф < 1.7м",
    cupbMax20: "Шкаф 1.7м - 2.0м",
    tumbAngleCupb: "Угл. шкаф + тумба",
  }

};

function priceRowName( elName, sizeName ) {

  const label = shortNames?.[ elName ];

  if ( !label ) { return `${ elName } ${ sizeName }`; }

  if ( typeof label === 'object' ) {

    return Object.prototype.hasOwnProperty.call( label, sizeName )
      ? label[ sizeName ]
      : `${ elName } ${ sizeName }`;

  }

  if ( label.indexOf( `$X$` ) !== -1 ) {

    return label.replace( `$X$`, sizeName );

  }

  return `${ label } ${ sizeName }`;

}

function isMultiPriceValue( price ) {

  return typeof price === 'string' && price.indexOf( '{' ) !== -1;

}

function displayUnit( unit ) {

  return unit?.replace( "п.м.", "пм" );

}

const Prices = () => {

  const navigate = useNavigate();
  const [ globalState, globalActions ] = useGlobal();
  const { user_id, user_role, company_pricelist } = globalState;
  const [ loading, setLoading ] = useState( false );
  const [ loadError, setLoadError ] = useState( '' );
  const [ hasLoaded, setHasLoaded ] = useState( false );
  const loadedForUser = useRef( '' );

  const role = Number( user_role );
  const canEdit = role === 1 || role === 2;
  const showLoading = loading || ( canEdit && !!user_id && !hasLoaded && !loadError );

  useEffect(() => {

    if ( !user_id ) { return; }
    if ( !canEdit ) { navigate( '/' ); }

  }, [ user_id, canEdit, navigate ]);

  useEffect(() => {

    if ( !user_id || !canEdit ) { return; }
    if ( loadedForUser.current === String( user_id ) ) { return; }

    loadedForUser.current = String( user_id );

    let cancelled = false;

    setLoading( true );
    setHasLoaded( false );
    setLoadError( '' );

    globalActions.prices.load().then(( result ) => {

      if ( cancelled ) { return; }

      setLoading( false );
      setHasLoaded( true );
      setLoadError( result?.success || result?.tariffRequired ? '' : ( result?.text || 'Не удалось загрузить прайс' ) );

    });

    return () => {

      cancelled = true;

      if ( loadedForUser.current === String( user_id ) ) {

        loadedForUser.current = '';

      }

    };

  }, [ user_id, canEdit, globalActions ]);

  async function saveItem( fields ) {

    return globalActions.prices.save( fields );

  }

  function retry() {

    setLoading( true );
    setLoadError( '' );

    globalActions.prices.load().then(( result ) => {

      setLoading( false );
      setHasLoaded( true );
      setLoadError( result?.success || result?.tariffRequired ? '' : ( result?.text || 'Не удалось загрузить прайс' ) );

    });

  }

  if (user_id && !canEdit) return null;

  return (

    <TariffGate feature = "prices">

    <Container className = "company-prices">

      <div className = "company-prices__head">

        <h1 className = "company-prices__title">Настроить цены</h1>

      </div>

      { !user_id && <p className = "company-prices__status">Загрузка...</p> }

      { user_id && canEdit && loadError && (

        <div className = "company-prices__status">

          <p className = "company-prices__error">{ loadError }</p>

          <button type = "button" className = "company-prices__retry" onClick = { retry }>

            Повторить

          </button>

        </div>

      )}

      { user_id && canEdit && showLoading && (

        <p className = "company-prices__status">Загрузка...</p>

      )}

      { user_id && canEdit && hasLoaded && !loading && !loadError && company_pricelist.length === 0 && (

        <p className = "company-prices__status">Прайс пока пуст</p>

      )}

      { user_id && canEdit && hasLoaded && !loading && !loadError && company_pricelist.length > 0 && (

        <div className = "company-prices__list">

          <div className = "company-prices__columns">

            <div />
            <div>Общая цена</div>
            <div>За работу</div>

          </div>

          { company_pricelist.map(( group, groupIndex ) => group.id === "sills" ? null : (

            <PriceGroup

              key = { group.id || group.name || groupIndex }
              group = { group }
              onSave = { saveItem }

            />

          ))}

        </div>

      )}

    </Container>

    </TariffGate>

  );

};

const PriceGroup = ({ group, onSave }) => {

  const content = group.content || [];
  const contentWithOnePrice = content.filter( el => !isMultiPriceValue( el?.price ) );
  const contentWithMultiPrice = content.filter( el => isMultiPriceValue( el?.price ) );

  return (

    <section className = "company-prices__group">

      <h2 className = "company-prices__group-title">{ group.name }:</h2>

      { contentWithOnePrice.map(( el ) => (

        <PriceRow

          key = { `${ el?.pricelistType || group.type }-${ el.id }-${ el.price }-${ el.pricePerJob }` }
          text = { el.name }
          unit = { displayUnit( el.unit ) }
          price = { el.price }
          pricePerJob = { el.pricePerJob }
          onSave = {( price, pricePerJob ) => onSave({

            pricelist_type: el?.pricelistType || group.type,
            target_id: el.id,
            price,
            pricePerJob,
            isMultiPrice: false,

          })}

        />

      ))}

      { contentWithMultiPrice.map(( el ) => (

        <MultiPrice

          key = { `${ el?.pricelistType || group.type }-${ el.id }-${ el.price }-${ el.pricePerJob }` }
          item = { el }
          type = { group.type }
          onSave = { onSave }

        />

      ))}

    </section>

  );

};

const MultiPrice = ({ item, type, onSave }) => {

  let pricesJSON;

  try {

    pricesJSON = JSON.parse( item.price );

  } catch ( err ) {

    return null;

  }

  if ( !pricesJSON || typeof pricesJSON !== 'object' || Array.isArray( pricesJSON ) ) { return null; }

  const names = Object.keys( pricesJSON );
  let pricesPerJobJSON = {};

  if ( isMultiPriceValue( item.pricePerJob ) ) {

    try {

      const parsedJob = JSON.parse( item.pricePerJob );

      if ( parsedJob && typeof parsedJob === 'object' && !Array.isArray( parsedJob ) ) {

        pricesPerJobJSON = parsedJob;

      }

    } catch ( err ) {

      pricesPerJobJSON = {};

    }

  }

  return (

    <div className = "company-prices__multi">

      <h3 className = "company-prices__group-title">{ item.name }:</h3>

      { names.map(( size ) => (

        <PriceRow

          key = { `${ type }-${ item.id }-${ size }-${ pricesJSON[ size ] }-${ pricesPerJobJSON?.[ size ] }` }
          text = { priceRowName( item.name, size ) }
          unit = { displayUnit( item.unit ) }
          price = { pricesJSON[ size ] }
          pricePerJob = { pricesPerJobJSON?.[ size ] || 0 }
          onSave = {( price, pricePerJob ) => onSave({

            pricelist_type: type,
            target_id: item.id,
            price,
            pricePerJob,
            isMultiPrice: true,
            multiPriceIndex: size,

          })}

        />

      ))}

    </div>

  );

};

export default Prices;
